import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePwaInstall } from '../hooks/usePwaInstall.js';
import InstallAppModal from './InstallAppModal.jsx';

/**
 * FAB "Install Aplikasi Exotic Pet World" — kondisional:
 * - Tidak render apa-apa kalau browser belum sempat memicu beforeinstallprompt
 *   DAN platformnya bukan mobile yang bisa dipandu instal manual (mis.
 *   aplikasi sudah terpasang, atau desktop browser yang memang tidak
 *   mendukung instalasi PWA sama sekali).
 * - Otomatis hilang begitu aplikasi sudah terpasang (event `appinstalled`
 *   atau saat dibuka dalam mode standalone).
 * - Menampilkan dialog kustom (InstallAppModal) dulu sebelum memicu
 *   konfirmasi native OS, atau menampilkan panduan manual step-by-step
 *   kalau browser/OS tidak mendukung `beforeinstallprompt` sama sekali
 *   (mis. Android 5.0 ke bawah, browser non-Chromium, iOS Safari).
 *
 * Posisi: fixed kiri-bawah. Di mobile digeser ke atas BottomNav
 * (yang fixed bottom-4 + tinggi ~64px) supaya tidak tumpang tindih;
 * di desktop (tanpa BottomNav) menempel dekat tepi bawah. Di halaman
 * artikel, BottomNav mobile disembunyikan (lihat BottomNav.jsx), jadi
 * tombol ini ikut turun ke posisi dekat tepi bawah juga (sama seperti desktop).
 *
 * Radius sengaja ditulis eksplisit dalam px (bukan `rounded-full`), dengan
 * tinggi tombol yang juga fixed (h-12 = 48px). `rounded-full` (9999px)
 * membuat animasi border-radius terasa "patah": browser meng-clamp nilai
 * render-nya ke setengah tinggi elemen, jadi sebagian besar durasi transisi
 * dari 9999px -> nilai hover tidak terlihat berubah, lalu nyentak cepat di
 * ujung. Dengan base radius = 24px (persis setengah tinggi 48px, tampilannya
 * identik dengan rounded-full tapi tanpa dead-zone itu), morph ke 14px saat
 * hover dan 20px saat active jadi mulus dan kontinu dari awal sampai akhir.
 */
export default function InstallAppButton() {
  const { installable, manualInstallable, promptInstall } = usePwaInstall();
  const [modalOpen, setModalOpen] = useState(false);
  const location = useLocation();
  const isArticle = location.pathname.startsWith('/artikel/');

  if (!installable && !manualInstallable) return null;

  const mode = installable ? 'native' : 'manual';

  const handleConfirm = async () => {
    await promptInstall();
    setModalOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        className={`fixed left-4 ${isArticle ? 'bottom-6' : 'bottom-24 nav-desktop:bottom-6'} z-50 flex items-center gap-2 h-12 pl-3 pr-4 rounded-[24px] text-white text-[13px] font-extrabold text-left leading-tight shadow-[0_8px_24px_rgba(0,0,0,0.25)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.32)] hover:rounded-[14px] active:rounded-[20px] active:scale-95 transition-all duration-300 ease-[var(--ease-spring)]`}
        style={{ backgroundColor: 'var(--accent)' }}
      >
        <span className="material-symbols-outlined text-2xl flex-shrink-0">install_mobile</span>
        <span className="max-w-[160px] line-clamp-2">Install Aplikasi Exotic Pet World</span>
      </button>

      <InstallAppModal
        open={modalOpen}
        mode={mode}
        onConfirm={handleConfirm}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}